import { type QuoteDetailData } from '@/lib/api'
import { LABOR_SUMMARY_NAME_TO_KEY_MAP } from '@/lib/utils'

export enum FieldServiceProductType {
  Inventory = 690970000,
  NonInventory = 690970001,
  Labor = 690970002,
  OtherCharge = 690970003,
}

export enum QuoteType {
  NewSale = 1,
  NewSaleDaisyCare = 5,
  ChangeOrder = 2,
  ServiceInvoice = 3,
  Budget = 4,
}

export enum QuoteSaleType {
  FixedBid = 1,
  TimeAndMaterial = 2,
}

export enum ProposalSummaryDisplayMode {
  None = 950120000,
  ProductsAndLabor = 950120001,
  SystemsIncludingLabor = 950120002,
}

export enum ProposalTotalsDisplayMode {
  Subtotal = 950120000,
  ProductsAndLabor = 950120001,
}

export enum QuoteChangeOrderType {
  External = 1,
  Internal = 2,
}

// Values encode the warranty length in years (2 is reserved for a future two-year option)
export enum QuoteWarrantyTerm {
  OneYear = 1,
  ThreeYears = 3,
}

export enum QuoteStateCode {
  Draft = 0,
  Active = 1,
  Won = 2,
  Closed = 3,
}

export const QUOTE_STATE_LABELS: Record<QuoteStateCode, string> = {
  [QuoteStateCode.Draft]: 'Draft',
  [QuoteStateCode.Active]: 'Active',
  [QuoteStateCode.Won]: 'Won',
  [QuoteStateCode.Closed]: 'Closed',
}

export enum QuoteStatusCode {
  Draft_InProgress = 1,
  Draft_Rebasing = 580080002,
  Active_InProgress = 2,
  Active_Open = 3,
  Won_Won = 4,
  Won_Applied = 580080001,
  Closed_Lost = 5,
  Closed_Canceled = 6,
  Closed_Revised = 7,
}

export enum PaymentMilestoneStateCode {
  Active = 0,
  Inactive = 1,
}

// Nodus PayFabric status text values — proposal-api keys on these exact strings
// (see create_paylink_for_first_payment_milestone.py in proposal-api).
export const PAYLINK_STATUS = {
  WaitingForPayment: 'Waiting For Payment',
  Paid: 'Paid',
} as const

export enum PaymentMilestoneStatus {
  Open = 1,
  Inactive = 2,
  ToBeInvoiced = 309890001,
  WaitingForPayment = 309890002,
  Paid = 309890003,
}

export enum ProductStatusReason {
  Active = 1,
  Retired = 2,
  Draft = 0,
  'Under Revision' = 3,
  Discontinued = 580080001,
  Duplicate = 580080002,
}

export type Account = {
  customertypecode: number
  address1_latitude: number | null
  merged: boolean
  dsy_filescreated: boolean | null
  dsy_mobilephoneprimarycontact: null | string
  statecode: number
  emailaddress1: null | string
  exchangerate: number
  address1_composite: null | string
  dsy_hotnote: null | string
  name: null | string
  opendeals: number
  modifiedon: Date
  dsy_video: null | string
  _owninguser_value: string
  _primarycontactid_value: null | string
  dsy_audio: null | string
  openrevenue_state: number
  address1_longitude: number | null
  donotpostalmail: boolean
  accountratingcode: number
  dsy_businessphoneprimarycontact: null | string
  marketingonly: boolean
  donotphone: boolean
  preferredcontactmethodcode: number
  _ownerid_value: string
  customersizecode: number
  openrevenue_date: Date
  openrevenue_base: number
  businesstypecode: number
  donotemail: boolean
  address2_shippingmethodcode: number
  openrevenue: number
  address1_addressid: string
  msdyn_gdproptout: boolean
  address2_freighttermscode: number
  statuscode: number
  createdon: Date
  msdyn_travelchargetype: number | null
  opendeals_state: number
  address1_stateorprovince: string | null
  dsy_phonenumberprimarycontact: null | string
  donotsendmm: boolean
  donotfax: boolean
  dsy_calculatedbranchname: string | null
  donotbulkpostalmail: boolean
  versionnumber: number
  address1_line1: null | string
  creditonhold: boolean
  address1_addresstypecode: number | null
  _transactioncurrencyid_value: string
  accountid: string
  dsy_shades: null | string
  donotbulkemail: boolean
  _modifiedby_value: string
  followemail: boolean
  shippingmethodcode: number
  _createdby_value: string
  dsy_emailprimarycontact: null | string
  address1_city: null | string
  territorycode: number
  address2_addresstypecode: number
  dsy_migrated: boolean
  _dsy_branch_value: null | string
  msdyn_taxexempt: boolean | null
  participatesinworkflow: boolean
  accountclassificationcode: number
  _owningbusinessunit_value: string
  address2_addressid: string
  address1_postalcode: null | string
  _defaultpricelevelid_value: null | string
  opendeals_date: Date
  _dsy_billtocontact_value: null
  aging60: null
  dsy_picture_timestamp: null
  dsy_picture: null
  address2_upszone: null
  sharesoutstanding: null
  address2_stateorprovince: null
  description: null | string
  _msdyn_salestaxcode_value: null
  msdyncrm_insights_placeholder: null
  websiteurl: null | string
  dsy_companyname: null | string
  dsy_surveycounter: number | null
  address2_longitude: null
  address2_telephone2: null
  entityimage: null
  entityimage_timestamp: null
  _msdyn_segmentid_value: null
  marketcap_base: null
  aging90_base: null
  address1_line3: null | string
  address1_telephone1: null | string
  _msdyn_salesaccelerationinsightid_value: null
  fax: null
  cr640_businessname: null | string
  stageid: null
  _preferredsystemuserid_value: null
  msevtmgt_rentalcarprovider: number | null
  address2_telephone1: null | string
  emailaddress2: null
  dsy_access: null
  address2_utcoffset: null
  adx_modifiedbyusername: null
  address2_city: null | string
  numberofemployees: null
  stockexchange: null
  traversedpath: null
  _msdyn_workhourtemplate_value: null
  dsy_rack: null
  dsy_power: null
  timespentbymeonemailandmeetings: null
  _msdyn_accountkpiid_value: null | string
  address2_country: null
  address2_name: null | string
  utcconversiontimezonecode: null
  telephone1: null | string
  dsy_lighting: null
  address2_county: null
  address2_postofficebox: null
  telephone2: null | string
  address2_latitude: null
  address1_fax: null | string
  entityimageid: null
  address2_fax: null | string
  dsy_migratedaccountstatus: null
  address1_shippingmethodcode: null
  lastusedincampaign: null
  msdyn_travelcharge: null
  address1_telephone3: null | string
  entityimage_url: null
  marketcap: null
  address1_freighttermscode: null
  address1_primarycontactname: null
  _parentaccountid_value: null
  address1_line2: null | string
  aging30_base: null
  address2_primarycontactname: null
  _slaid_value: null
  creditlimit: null
  industrycode: null
  processid: null | string
  dsy_servicezone: null
  dsy_signage: null
  address2_line2: null | string
  address2_telephone3: null
  aging90: null
  _msa_managingpartnerid_value: null
  _slainvokedid_value: null
  _owningteam_value: null
  dsy_security: null
  address2_line1: null | string
  address1_postofficebox: null
  msdyn_externalaccountid: null
  dsy_floor: null
  address1_country: string | null
  dsy_roughin: null
  tickersymbol: null
  timezoneruleversionnumber: null
  msdyn_workorderinstructions: null
  _territoryid_value: null
  msdyn_travelcharge_base: null
  revenue_base: null
  _dsy_device_value: null
  _modifiedbyexternalparty_value: null
  adx_createdbyipaddress: null
  revenue: null
  adx_modifiedbyipaddress: null
  dsy_fixtures: null
  dsy_control: null
  _preferredserviceid_value: null
  paymenttermscode: null
  dsy_dtools_cloud_id: null | string
  dsy_network: null
  importsequencenumber: number | null
  address1_name: null | string
  _modifiedonbehalfby_value: null | string
  dsy_tigerpaw_number: null | string
  yominame: null
  creditlimit_base: null
  teamsfollowed: null
  primarytwitterid: null
  _preferredequipmentid_value: null
  address1_telephone2: null | string
  address1_county: null
  _msdyn_serviceterritory_value: null
  _createdonbehalfby_value: null | string
  dsy_pictureid: null
  address2_composite: null | string
  ftpsiteurl: null
  accountnumber: null | string
  aging30: null
  _msdyn_preferredresource_value: null
  _msdyn_billingaccount_value: null
  _originatingleadid_value: null | string
  address1_utcoffset: null
  dsy_surveytype: number | null
  primarysatoriid: null
  address1_upszone: null
  overriddencreatedon: null
  preferredappointmenttimecode: null
  preferredappointmentdaycode: null
  _createdbyexternalparty_value: null
  _masterid_value: null
  emailaddress3: null
  aging60_base: null
  dsy_accountsource: null | string
  dsy_camera: null
  dsy_daisycare: boolean | null
  ownershipcode: null
  address2_postalcode: null
  dsy_picture_url: null
  dsy_room: null
  accountcategorycode: null
  msdyn_primarytimezone: null
  lastonholdtime: null
  msdyn_taxexemptnumber: null
  msevtmgt_hotelgroup: number | null
  dsy_location: null
  dsy_conferencing: null
  telephone3: null
  address2_line3: null
  sic: null
  onholdtime: null
  adx_createdbyusername: null
}

export type Contact = {
  customertypecode: number
  address1_latitude: number | null
  merged: boolean
  territorycode: number
  emailaddress1: null | string
  haschildrencode: number
  preferredappointmenttimecode: number
  address1_composite: null | string
  msgdpr_gdprischild: boolean | null
  isbackofficecustomer: boolean
  modifiedon: Date
  msdyn_orgchangestatus: number
  _owninguser_value: string
  adx_profilealert: boolean
  msgdpr_donottrack: boolean | null
  address1_longitude: number | null
  donotpostalmail: boolean
  dsy_tradepartnerstatus: number | null
  marketingonly: boolean
  donotphone: boolean
  preferredcontactmethodcode: number
  adx_identity_locallogindisabled: boolean
  educationcode: number
  _ownerid_value: string
  description: null | string
  adx_identity_logonenabled: boolean
  customersizecode: number
  firstname: null | string
  adx_identity_lockoutenabled: boolean
  msdyncrm_rememberme: boolean | null
  dsy_companyname: null | string
  yomifullname: string
  address2_addresstypecode: number
  adx_profileisanonymous: boolean
  donotemail: boolean
  address2_shippingmethodcode: number
  fullname: string
  address1_addressid: string
  msdyn_gdproptout: boolean
  address2_freighttermscode: number
  statuscode: number
  createdon: Date
  msgdpr_gdprconsent: number | null
  lastname: null | string
  donotsendmm: boolean
  donotfax: boolean
  donotbulkpostalmail: boolean
  versionnumber: number
  adx_confirmremovepassword: boolean
  address1_line1: null | string
  creditonhold: boolean
  telephone2: null | string
  adx_identity_emailaddress1confirmed: boolean
  msdyn_isminor: boolean
  msdyn_isminorwithparentalconsent: boolean
  address3_addressid: string
  donotbulkemail: boolean
  adx_identity_twofactorenabled: boolean
  _modifiedby_value: string
  followemail: boolean
  shippingmethodcode: number
  _createdby_value: string
  address1_city: null | string
  dsy_type: number | null
  dsy_migrated: boolean
  contactid: string
  _dsy_branch_value: string
  msdyn_disablewebtracking: boolean
  msdyn_isassistantinorgchart: boolean
  adx_identity_mobilephoneconfirmed: boolean
  participatesinworkflow: boolean
  statecode: number
  _owningbusinessunit_value: string
  address2_addressid: string
  timespentbymeonemailandmeetings: null
  fax: null | string
  adx_modifiedbyipaddress: null
  address2_name: null
  dsy_tradepartnertype: number | null
  mspp_userpreferredlcid: null
  address1_county: null
  adx_modifiedbyusername: null
  address1_upszone: null
  _preferredequipmentid_value: null
  _transactioncurrencyid_value: null | string
  _msdyncrm_contactid_value: null
  dsy_channeldrilldown1: null
  _msgdpr_consentchangesourceformid_value: null
  exchangerate: number | null
  anniversary: null
  dsy_prefix: null
  _msdyncrm_segmentmemberid_value: null
  address3_postalcode: null
  dsy_association: null
  adx_createdbyusername: null
  utcconversiontimezonecode: null
  birthdate: null
  adx_identity_username: null
  address2_county: null
  address2_latitude: null
  accountrolecode: null
  yomimiddlename: null
  timezoneruleversionnumber: null
  subscriptionid: null
  _msdynmkt_emailid_value: null
  suffix: null
  adx_profilealertinstructions: null
  address2_telephone2: null
  _modifiedonbehalfby_value: null
  dsy_dtools_cloud_id: null
  leadsourcecode: number | null
  dsy_accountid: null
  entityimage: null
  externaluseridentifier: null
  address1_addresstypecode: null
  address3_primarycontactname: null
  address3_line2: null
  address2_primarycontactname: null
  _msevtmgt_contactid_value: null
  address2_line2: null
  mobilephone: null | string
  address3_telephone3: null
  traversedpath: null
  adx_createdbyipaddress: null
  aging60: null
  address1_stateorprovince: string | null
  address3_shippingmethodcode: null
  entityimageid: null
  address3_postofficebox: null
  _msdyn_segmentid_value: null
  address1_telephone2: null
  employeeid: null
  address2_postalcode: null
  spousesname: null
  familystatuscode: null
  address3_longitude: null
  stageid: null
  address2_postofficebox: null
  dsy_clientstatus: null
  department: null
  msdyncrm_marketingformsubmissiondateprecise: null
  ftpsiteurl: null
  salutation: null
  aging30: null
  adx_identity_lastsuccessfullogin: null
  address3_freighttermscode: null
  address1_utcoffset: null
  msdyn_portaltermsagreementdate: null
  adx_identity_securitystamp: null
  address1_country: string | null
  adx_identity_accessfailedcount: null
  address2_longitude: null
  company: null
  numberofchildren: null
  aging90: null
  _parentcustomerid_value: null | string
  address3_county: null
  _msgdpr_gdprparentid_value: null
  jobtitle: string | null
  nickname: null
  websiteurl: null
  address3_city: null
  adx_identity_newpassword: null
  entityimage_timestamp: null
  address3_line3: null
  gendercode: null
  telephone1: null | string
  _owningteam_value: null
  businesscard: null
  address2_country: null
  msdyn_decisioninfluencetag: null
  adx_timezone: null
  address3_line1: null
  address1_name: null
  _createdonbehalfby_value: null
  _defaultpricelevelid_value: null
  _cr640_opportunity_value: null
  _adx_preferredlanguageid_value: null
  adx_publicprofilecopy: null
  address2_city: null
  _msdyn_contactkpiid_value: null | string
  address2_telephone3: null
  _preferredsystemuserid_value: null
  telephone3: null
  _slaid_value: null
  address2_telephone1: null
  annualincome_base: null
  _msdynmkt_customerjourneyid_value: null
  annualincome: null
  paymenttermscode: null
  address1_telephone3: null
  lastusedincampaign: null
  dsy_duplicatecontactinactivestatus: null
  lastonholdtime: null
  _modifiedbyexternalparty_value: null
  adx_profilealertdate: null
  _msa_managingpartnerid_value: null
  address2_line3: null
  address3_utcoffset: null
  processid: null
  address3_upszone: null
  address3_name: null
  managerphone: null
  aging90_base: null
  aging30_base: null
  adx_organizationname: null | string
  address1_postalcode: null | string
  dsy_channeldrilldown2: null
  address2_stateorprovince: null
  address1_line3: null
  address1_line2: null | string
  creditlimit_base: null
  adx_preferredlcid: null
  yomifirstname: null
  home2: null
  onholdtime: null
  address1_fax: null
  adx_profilelastactivity: null
  address1_postofficebox: null
  address1_telephone1: null
  adx_profilemodifiedon: null
  address1_shippingmethodcode: null
  address3_latitude: null
  governmentid: null
  managername: null
  _slainvokedid_value: null
  _cr640_lead_value: null | string
  address3_fax: null
  adx_identity_passwordhash: null
  pager: null
  entityimage_url: null
  preferredappointmentdaycode: null
  address3_addresstypecode: null
  address2_utcoffset: null
  assistantname: null
  _originatingleadid_value: null
  _dsy_billtocontact_value: null
  business2: null
  yomilastname: null
  emailaddress3: null
  _msdynmkt_marketingformid_value: null
  assistantphone: null
  dsy_channel: null
  teamsfollowed: null
  importsequencenumber: number | null
  msevtmgt_aadobjectid: null
  dsy_servicetype: null
  dsy_tigerpaw_number: null
  address2_line1: null
  address3_stateorprovince: null
  dsy_channeldrilldown3: null
  _msdyncrm_customerjourneyid_value: null
  address3_telephone1: null
  address1_freighttermscode: null
  _createdbyexternalparty_value: null
  emailaddress2: null
  msdyncrm_insights_placeholder: null
  _masterid_value: null
  _msdyncrm_emailid_value: null
  _preferredserviceid_value: null
  address3_composite: null
  callback: null
  address3_telephone2: null
  aging60_base: null
  overriddencreatedon: null
  dsy_landingpagegroup: null
  address3_country: null
  dsy_landingpage: null
  _msdyncrm_marketingformid_value: null
  dsy_contactnumber: null
  childrensnames: null
  _accountid_value: null
  address2_upszone: null
  middlename: null
  businesscardattributes: null
  msdyn_primarytimezone: null
  address1_primarycontactname: null
  _msdyncrm_marketingpageid_value: null
  address2_composite: null
  _msevtmgt_originatingeventid_value: null
  _parentcontactid_value: null
  creditlimit: null
  address2_fax: null
  adx_identity_lockoutenddate: null
}

export type SystemUser = {
  address2_addresstypecode: number | null
  islicensed: boolean
  accessmode: number
  systemuserid: string
  windowsliveid: null | string
  issyncwithdirectory: boolean
  address2_shippingmethodcode: number | null
  applicationid: null | string
  modifiedon: Date
  preferredaddresscode: number | null
  displayinserviceviews: boolean | null
  defaultfilterspopulated: boolean
  address1_shippingmethodcode: number | null
  preferredemailcode: number | null
  lastname: string
  msdyn_usertype: number | null
  caltype: number
  _calendarid_value: null | string
  firstname: null | string
  userlicensetype: number
  yomifullname: null | string
  _businessunitid_value: string
  incomingemaildeliverymethod: number
  setupuser: boolean
  fullname: string
  address1_addressid: null | string
  msdyn_gdproptout: boolean | null
  createdon: Date
  msdyn_isexpertenabledforswarm: boolean | null
  deletedstate: number
  defaultodbfoldername: string | null
  _defaultmailbox_value: null | string
  invitestatuscode: number
  versionnumber: number
  isintegrationuser: boolean
  outgoingemaildeliverymethod: number
  address1_addresstypecode: number | null
  _modifiedby_value: null | string
  _createdby_value: null | string
  preferredphonecode: number | null
  domainname: string
  msdyn_botprovider: number | null
  azurestate: number
  identityid: number
  isdisabled: boolean
  address2_addressid: null | string
  emailrouteraccessapproval: number
  organizationid: string
  internalemailaddress: null | string
  isemailaddressapprovedbyo365admin: boolean
  _queueid_value: null | string
  ownerid: string
  yammeruserid: null
  nickname: null
  address1_upszone: null
  address2_city: null
  address1_postofficebox: null
  importsequencenumber: null
  utcconversiontimezonecode: null
  overriddencreatedon: null
  _siteid_value: null
  stageid: null
  photourl: null
  address1_latitude: number | null
  address1_utcoffset: null
  yomifirstname: null
  msdyn_gridwrappercontrolfield: null
  address2_fax: null
  _transactioncurrencyid_value: null
  governmentid: null
  address2_line1: null
  msdyn_botsecretkeys: null
  address1_telephone3: null
  address1_telephone2: null
  address1_telephone1: null | string
  msdyn_owningenvironmentid: null
  address2_postofficebox: null
  address2_latitude: null
  processid: null
  address2_composite: null
  traversedpath: null
  address1_city: null | string
  _positionid_value: null | string
  address2_line2: null
  address2_stateorprovince: null
  sharepointemailaddress: null
  address2_postalcode: null
  entityimage_url: null
  address1_composite: null | string
  msdyn_botapplicationid: null
  jobtitle: null
  timezoneruleversionnumber: null
  address2_telephone3: null
  address2_telephone2: null
  address2_telephone1: null
  address1_postalcode: null | string
  address2_upszone: null
  userpuid: null | string
  address2_line3: null
  msdyn_bothandle: null
  address1_country: null
  msdyn_botdescription: null
  personalemailaddress: null
  address2_longitude: null
  _modifiedonbehalfby_value: null | string
  address1_line2: null
  address1_county: null
  _territoryid_value: null
  address1_fax: null
  _createdonbehalfby_value: null
  address2_name: null
  disabledreason: null
  address2_utcoffset: null
  applicationiduri: null | string
  _mobileofflineprofileid_value: null
  address1_line1: null | string
  address2_county: null
  msdyn_agentType: null
  address1_line3: null
  azuredeletedon: Date | null
  azureactivedirectoryobjectid: null | string
  msdyn_botendpoint: null
  address1_stateorprovince: null | string
  _parentsystemuserid_value: null
  entityimage_timestamp: null
  title: null | string
  mobilephone: null | string
  _msdyn_defaultpresenceiduser_value: null | string
  employeeid: null
  exchangerate: null
  skills: null
  entityimageid: null
  passporthi: null
  yomilastname: null
  passportlo: null
  yomimiddlename: null
  homephone: null
  address1_name: null
  address1_longitude: number | null
  yammeremailaddress: null
  entityimage: null
  middlename: null
  salutation: null
  msdyn_capacity: number | null
  address2_country: null
  mobilealertemail: null
}

export type Product = {
  isreparented: boolean
  productid: string
  statuscode: number
  dsy_rackmounted: boolean
  iskit: boolean
  currentcost: number | null
  createdon: Date
  importsequencenumber: number
  dsy_depth: number | null
  statecode: number
  price_base: number | null
  productnumber: string
  dsy_width: number | null
  msdyn_taxable: boolean
  producttypecode: number
  dsy_weight: number | null
  name: string
  _parentproductid_value: string
  price: number | null
  _transactioncurrencyid_value: string
  productstructure: number
  isstockitem: boolean
  versionnumber: number
  dsy_partnumber: null | string
  _defaultuomscheduleid_value: string
  currentcost_base: number | null
  exchangerate: number
  description: null | string
  _dsy_manufacturerid_value: null | string
  _modifiedby_value: string
  _modifiedonbehalfby_value: null | string
  modifiedon: Date
  msdyn_gdproptout: boolean
  producturl: null | string
  hierarchypath: string
  _defaultuomid_value: string
  dsy_defaultphase: number | null
  quantitydecimal: number
  _createdby_value: string
  _organizationid_value: string
  msdyn_purchasename: string
  msdyn_converttocustomerasset: boolean
  dsy_height: number | null
  utcconversiontimezonecode: null
  processid: null
  stockweight: null
  vendorname: null
  quantityonhand: null
  entityimage: null
  validtodate: null
  dsy_watts: null
  validfromdate: null
  vendorpartnumber: null
  dsy_volts: number | null
  _msdyn_defaultvendor_value: null
  entityimageid: null
  entityimage_timestamp: null
  msdyn_fieldserviceproducttype: FieldServiceProductType | null
  timezoneruleversionnumber: null
  size: null
  standardcost_base: null
  dsy_manufacturerlookupcontrol: null
  vendorid: null
  _msdyn_transactioncategory_value: null
  _createdonbehalfby_value: null
  msdyn_upccode: null
  overriddencreatedon: null
  stageid: null
  entityimage_url: null
  standardcost: null
  dmtimportstate: null
  _modifiedbyexternalparty_value: null
  stockvolume: null
  suppliername: null
  traversedpath: null
  _pricelevelid_value: null
  dsy_amps: null
  dsy_btu: null
  dsy_rackunits: null
  _createdbyexternalparty_value: null
  _subjectid_value: null
  dsy_engineeringlabor: number | null
  dsy_installationlabor: number | null
  dsy_roughinlabor: number | null
  dsy_programminglabor: number | null
  dsy_projectmanagementlabor: number | null
}

export type Quote = {
  msdyn_estimatedschedule: number
  totaltax: number
  _pricelevelid_value: null | string
  exchangerate: number
  _opportunityid_value: null | string
  name: string
  msdyn_totalcost: number
  totallineitemamount: number
  msdyn_totalnonchargeablecostrollup_state: number
  _owningbusinessunit_value: string
  msdyn_totalamount_base: number
  _owninguser_value: string
  totalamount: number
  _ownerid_value: string
  msdyn_grossmargin: number
  msdyn_totalamount: number
  msdyn_totalchargeablecostrollup_state: number
  msdyn_customerbudgetrollup_state: number
  msdyn_profitabilitymetric: number | null
  quoteid: string
  totalamountlessfreight: number
  quotecreationmethod: number
  msdyn_totalchargeablecostrollup: number
  msdyn_totalnonchargeablecostrollup: number
  totalamountlessfreight_base: number
  totaldiscountamount: number
  statuscode: number
  createdon: Date
  msdyn_budgetmetric: number
  msdyn_customerbudgetrollup: number
  msdyn_ordertype: number | null
  msdyn_totalnonchargeablecostrollup_base: number | null
  totaltax_base: number
  totallineitemamount_base: number
  msdyn_totalnonchargeablecostrollup_date: Date
  willcall: boolean
  totalamount_base: number
  msdyn_estimatedbudget: number
  versionnumber: number
  msdyn_customerbudgetrollup_date: Date
  modifiedon: Date
  totallineitemdiscountamount: number
  _transactioncurrencyid_value: string
  totaldiscountamount_base: number
  msdyn_customerbudgetrollup_base: number | null
  quotenumber: string
  msdyn_adjustedgrossmargin: number
  msdyn_totalcost_base: number
  _modifiedby_value: string
  msdyn_estimatedcompletionrollup_date: Date
  revisionnumber: number
  _createdby_value: string
  msdyn_competitive: number
  pricingerrorcode: number
  msdyn_profitability: number
  totallineitemdiscountamount_base: number
  _modifiedonbehalfby_value: null | string
  msdyn_totalchargeablecostrollup_date: Date
  statecode: number
  msdyn_budgetmetric_base: number
  skippricecalculation: number
  _customerid_value: string
  msdyn_totalchargeablecostrollup_base: number | null
  billto_line2: null
  discountamount: number | null
  expireson: null
  msdyn_quotelineenddate: null
  paymenttermscode: null
  processid: null | string
  msdyn_estimatedcost: null
  billto_fax: null
  _msdyn_contractorganizationalunitid_value: null | string
  requestdeliveryby: null
  freightamount_base: null
  msdyn_feasible: number | null
  _createdonbehalfby_value: null
  billto_telephone: null
  timezoneruleversionnumber: null
  traversedpath: null
  shipto_telephone: null
  shipto_contactname: null
  utcconversiontimezonecode: null
  onholdtime: null
  shipto_line2: null
  shipto_composite: null
  billto_city: null
  shipto_line1: null
  discountamount_base: number | null
  billto_contactname: null
  closedon: null
  billto_line1: null
  shipto_postalcode: null
  _slainvokedid_value: null
  shipto_city: null
  freightamount: null
  shipto_fax: null
  stageid: null
  importsequencenumber: null
  billto_composite: null
  _accountid_value: null
  shipto_freighttermscode: null
  msdyn_invoicesetuptotals: null
  _slaid_value: null
  billto_postalcode: null
  overriddencreatedon: null
  shipto_addressid: null
  lastonholdtime: null
  discountpercentage: number | null
  _contactid_value: null
  effectiveto: null
  billto_country: null
  msdyn_quotelinestartdate: null
  shipto_line3: null
  freighttermscode: null
  msdyn_nottoexceedlimit: number | null
  billto_line3: null
  msdyn_estimatedcompletionrollup: Date | null
  billto_addressid: null
  _owningteam_value: null
  _campaignid_value: null
  _msdyn_accountmanagerid_value: null | string
  msdyn_estimatedcost_base: null
  description: null
  msdyn_estimatedcompletionrollup_state: number | null
  shipto_name: null
  effectivefrom: null
  emailaddress: null
  shippingmethodcode: null
  billto_name: null
  shipto_stateorprovince: null
  msdyn_invoicesetuptotals_base: null
  shipto_country: null
  billto_stateorprovince: null
  msdyn_nottoexceedlimit_base: number | null
  msdyn_estimatedquotemargin: number | null
  _msdyn_account_value: null | string
  msdyn_schedulemetric: null
  dsy_approvalsignature: null | string
  dsy_approvalsignature_url: null | string
  dsy_approvalsignaturedatetime: null | string
  dsy_activatedon: string | null
  dsy_suppliespricerate?: number
  dsy_suppliescostrate?: number
}

export type QuoteDetail = {
  lineitemnumber: number | null
  msdyn_feeamount: number
  quotedetailname: string
  requestdeliveryby: Date | null
  msdyn_feeamount_base: number
  exchangerate: number
  isproductoverridden: boolean
  sequencenumber: number
  ispriceoverridden: boolean | null
  _owninguser_value: string
  skippricecalculation: number
  priceperunit: number | null
  extendedamount: number | null
  producttypecode: number
  _ownerid_value: string
  description: null | string
  msdyn_importdetailsfromagreement: boolean | null
  _quoteid_value: string
  createdon: Date
  quotecreationmethod: number
  msdyn_includeexpense: boolean | null
  msdyn_includematerial: boolean | null
  msdyn_linetype: number | null
  msdyn_includetime: boolean | null
  _productid_value: string | null
  productnumber: string | null
  msdyn_includefee: boolean | null
  extendedamount_base: number | null
  quotestatecode: number
  baseamount_base: number | null
  msdyn_linedescription: string
  willcall: boolean
  versionnumber: number
  baseamount: number | null
  modifiedon: Date
  _owningbusinessunit_value: string
  _transactioncurrencyid_value: string
  _modifiedby_value: string
  _uomid_value: null | string
  priceperunit_base: number
  quantity: number
  _createdby_value: string
  msdyn_costamount_base: number | null
  msdyn_costpriceperunit_base: number | null
  volumediscountamount_base: number
  msdyn_costpriceperunit: number | null
  pricingerrorcode: number
  msdyn_costamount: number | null
  volumediscountamount: number
  productname: string
  msdyn_taxable: boolean | null
  quotedetailid: string
  msdyn_opportunityline: null | string
  propertyconfigurationstatus: number
  manualdiscountamount_base: null
  msdyn_invoicescheduletype: number | null
  msdyn_feepercentage: null
  _msdyn_project_value: null | string
  manualdiscountamount: number | null
  _msdyn_salestaxcode_value: null
  _msdyn_agreement_value: null
  tax: number | null
  importsequencenumber: null
  _createdonbehalfby_value: null
  msdyn_enddate: null
  shipto_line3: null
  shipto_telephone: null
  msdyn_duration: null
  shipto_fax: null
  shipto_stateorprovince: null
  shipto_name: null
  _owningteam_value: null
  shipto_addressid: null
  msdyn_costtotal_base: number | null
  _msdyn_serviceterritory_value: null
  _salesrepid_value: null
  msdyn_nottoexceedlimit_base: null
  msdyn_estimatedrevenue: null
  shipto_contactname: null
  msdyn_nottoexceedlimit: null
  shipto_line1: null
  timezoneruleversionnumber: number | null
  overriddencreatedon: null
  _modifiedonbehalfby_value: null | string
  parentbundleid: null
  msdyn_discountpercentage: number | null
  msdyn_costtotal: number | null
  tax_base: number | null
  msdyn_billingmethod: number | null
  msdyn_includedtasks: number | null
  shipto_freighttermscode: null
  shipto_line2: null
  shipto_postalcode: null
  _msdyn_pricelist_value: null
  msdyn_estimatedcost: number | null
  msdyn_budgetamount_base: number | null
  productdescription: null | string
  msdyn_estimatedrevenue_base: null
  msdyn_estimatedmargin: null
  shipto_country: null
  _msdyn_invoicefrequency_value: null | string
  _parentbundleidref_value: null
  msdyn_budgetamount: number | null
  utcconversiontimezonecode: null
  msdyn_startdate: null
  _msdyn_serviceaccount_value: null
  productassociationid: null
  shipto_city: null
  msdyn_billingstartdate: Date | null
  msdyn_estimatedcost_base: null
  _dsy_cpqfields_value: string | null
}

export type UpsertQuoteDetailBody = Partial<
  Omit<
    QuoteDetail,
    | '_owninguser_value'
    | '_ownerid_value'
    | '_quoteid_value'
    | '_productid_value'
    | '_owningbusinessunit_value'
    | '_transactioncurrencyid_value'
    | '_modifiedby_value'
    | '_uomid_value'
    | '_createdby_value'
    | '_msdyn_project_value'
    | '_msdyn_salestaxcode_value'
    | '_msdyn_agreement_value'
    | '_createdonbehalfby_value'
    | '_owningteam_value'
    | '_msdyn_serviceterritory_value'
    | '_salesrepid_value'
    | '_modifiedonbehalfby_value'
    | '_msdyn_pricelist_value'
    | '_msdyn_invoicefrequency_value'
    | '_parentbundleidref_value'
    | '_msdyn_serviceaccount_value'
    | '_dsy_cpqfields_value'
  > & {
    'owninguser@odata.bind': string
    'ownerid@odata.bind': string
    'quoteid@odata.bind': string
    'productid@odata.bind': string
    'owningbusinessunit@odata.bind': string
    'transactioncurrencyid@odata.bind': string
    'modifiedby@odata.bind': string
    'uomid@odata.bind': string
    'createdby@odata.bind': string
    'msdyn_project@odata.bind': string
    'msdyn_SalesTaxCode@odata.bind': string
    'msdyn_agreement@odata.bind': string
    'createdonbehalfby@odata.bind': string
    'owningteam@odata.bind': string
    'msdyn_serviceterritory@odata.bind': string
    'salesrepid@odata.bind': string
    'modifiedonbehalfby@odata.bind': string
    'msdyn_pricelist@odata.bind': string
    'msdyn_invoicefrequency@odata.bind': string
    'parentbundleidref@odata.bind': string
    'msdyn_serviceaccount@odata.bind': string
    'dsy_cpqfields@odata.bind': string
  }
>

export type SalesOrderDetail = {
  salesorderdetailid: string
  priceperunit: number
  extendedamount: number
  description: string
  productnumber: string
  extendedamount_base: number
  tax: number
  baseamount_base: number
  salesorderdetailname: string
  tax_base: number
  baseamount: number
  ispriceoverridden: boolean
  priceperunit_base: number
  quantity: number
  productname: string
  productdescription: string | null
  msdyn_costpriceperunit_base: number | null
  msdyn_costamount_base: number | null
  msdyn_costamount: number | null
  lineitemnumber: number | null
  msdyn_costpriceperunit: number | null
  _salesorderid_value: string
  _productid_value: string
  _uomid_value: string
  _quotedetailid_value: string
  _dsy_cpqfields_value: string | null
}

export type QuoteDetailFilters =
  | {
      previousQuote?: boolean
      quoteDetail?: {
        quoteDetail: QuoteDetailData
        includeAccessories: boolean
        unitOnly?: boolean
        /**
         * Package header only: widen the set to the header plus its transitive
         * member set (members, then those members' accessories), so the summary
         * engine derives the package total with the same math as every other
         * roll-up. Nothing is stored — the header itself is always $0.
         */
        includeMembers?: boolean
      }
      roomId?: never
      systemId?: never
      unassignedSystem?: never
      laborKey?: never
      onlySummaryDetails?: never
      /** The table's item search: a row's accessories count only when they match it. */
      search?: string | null
      includeChildRooms?: never
    }
  | {
      previousQuote?: boolean
      onlySummaryDetails?: 'all' | 'labor'
      roomId?: never
      systemId?: never
      unassignedSystem?: never
      laborKey?: never
      quoteDetail?: never
      search?: never
      includeChildRooms?: never
    }
  | {
      previousQuote?: boolean
      roomId?: string | null
      systemId?: number | null
      unassignedSystem?: boolean
      laborKey?: keyof typeof LABOR_SUMMARY_NAME_TO_KEY_MAP
      quoteDetail?: never
      onlySummaryDetails?: never
      search?: string | null
      /**
       * Select the room *group* (the room plus its direct child rooms) instead of
       * the exact room. Meaningful only together with `roomId`; ignored without
       * one. Consumers derive it (`getChildRooms(roomId, rooms).length > 0`) —
       * it is never stored.
       */
      includeChildRooms?: boolean
    }
  | null

export type TaxCode = {
  msdyn_taxcodeid: string
  msdyn_name: string
  msdyn_taxrate: number
  dsy_zipcodes: string
}
